import { supabase } from "@/integrations/supabase/client";

export type MediaItem = {
  id: string;
  storage_path: string;
  url: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  folder: string;
  tags: string[];
  title: string;
  alt_text: string;
  description: string;
  metadata_generated: boolean;
  created_at: string;
  updated_at: string;
};

/** WebP-Optimierung wie in Editable.tsx, aber als eigenständiger Helper. */
export async function optimizeToWebp(file: File, maxEdge = 1920, quality = 0.82): Promise<Blob> {
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml" || file.type === "image/gif") {
    return file;
  }
  try {
    const bmp = await createImageBitmap(file);
    const ratio = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * ratio);
    const h = Math.round(bmp.height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob((b) => res(b), "image/webp", quality)
    );
    return blob ?? file;
  } catch {
    return file;
  }
}

async function getImageSize(blob: Blob): Promise<{ width: number; height: number } | null> {
  try {
    const bmp = await createImageBitmap(blob);
    return { width: bmp.width, height: bmp.height };
  } catch { return null; }
}

export function sanitizeName(name: string) {
  return name
    .replace(/ä/g, "ae").replace(/Ä/g, "Ae")
    .replace(/ö/g, "oe").replace(/Ö/g, "Oe")
    .replace(/ü/g, "ue").replace(/Ü/g, "Ue")
    .replace(/ß/g, "ss")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // restliche Diakritika
    .replace(/[^a-zA-Z0-9.\-_]+/g, "_")
    .slice(0, 80);
}


export type UploadResult = MediaItem;

export type UploadOptions = {
  folder?: string;
  tags?: string[];
  generateMetadata?: boolean; // default true
  /** Unterordner im Storage (z. B. "popups", "sections/home"). Optional, separat vom Mediathek-Ordner. */
  pathPrefix?: string;
};

/**
 * Lädt eine Datei in den `cms-media`-Bucket, optimiert auf WebP, legt einen
 * Eintrag in `cms_media` an und triggert (optional) die Auto-Metadaten.
 */
export async function uploadToMediaLibrary(file: File, opts: UploadOptions = {}): Promise<UploadResult> {
  const optimized = await optimizeToWebp(file);
  const dims = await getImageSize(optimized);
  const prefix = opts.pathPrefix ? `${opts.pathPrefix.replace(/^\/+|\/+$/g, "")}/` : "library/";
  const baseName = file.name.replace(/\.[^.]+$/, "");
  const ext = optimized.type === "image/webp" ? "webp" : (file.name.split(".").pop() || "bin");
  const path = `${prefix}${Date.now()}-${sanitizeName(baseName)}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from("cms-media")
    .upload(path, optimized, { upsert: true, contentType: optimized.type || file.type, cacheControl: "31536000" });
  if (upErr) throw upErr;

  const { data: signed, error: signErr } = await supabase.storage
    .from("cms-media")
    .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
  if (signErr || !signed) throw signErr ?? new Error("URL fehlgeschlagen");

  const { data: { user } } = await supabase.auth.getUser();
  const insertPayload = {
    storage_path: path,
    url: signed.signedUrl,
    filename: file.name,
    mime_type: optimized.type || file.type || null,
    size_bytes: optimized.size,
    width: dims?.width ?? null,
    height: dims?.height ?? null,
    folder: opts.folder ?? "",
    tags: opts.tags ?? [],
    title: baseName,
    alt_text: "",
    description: "",
    metadata_generated: false,
    uploaded_by: user?.id ?? null,
  };

  const { data: row, error: insErr } = await (supabase as any)
    .from("cms_media")
    .insert(insertPayload)
    .select("*")
    .single();
  if (insErr) throw insErr;

  // Auto-Metadaten im Hintergrund anstoßen
  if (opts.generateMetadata !== false) {
    void generateMetadataFor(row as MediaItem).catch(() => { /* still gespeichert */ });
  }

  return row as MediaItem;
}

/** Macht eine ggf. relative URL für den Server-Fetch absolut. */
function absolutizeUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window !== "undefined") {
    try { return new URL(url, window.location.origin).toString(); } catch { /* noop */ }
  }
  return url;
}

function canGenerateImageMetadata(item: Pick<MediaItem, "filename" | "mime_type">): boolean {
  const mime = item.mime_type?.toLowerCase() ?? "";
  const filename = item.filename.toLowerCase();
  if (mime === "image/svg+xml" || filename.endsWith(".svg") || filename.endsWith(".gif")) return false;
  return mime.startsWith("image/") || /\.(jpe?g|png|webp|avif)$/i.test(filename);
}

async function imageUrlToDataUrl(url: string): Promise<string> {
  if (/^data:image\//i.test(url)) return url;
  const response = await fetch(absolutizeUrl(url));
  if (!response.ok) throw new Error(`Bild konnte nicht geladen werden (${response.status})`);
  const blob = await response.blob();
  if (!blob.type.startsWith("image/") || blob.type === "image/svg+xml") {
    throw new Error(`Keine unterstützte Bilddatei: ${blob.type || "unbekannt"}`);
  }
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Ruft die Edge-Function auf und schreibt die Felder in `cms_media` zurück. */
export async function generateMetadataFor(item: MediaItem): Promise<MediaItem | null> {
  if (!canGenerateImageMetadata(item)) return null;
  let imageUrl: string;
  try {
    // Edge Functions können lokale Preview-/Vite-Asset-URLs nicht zuverlässig abrufen.
    // Deshalb liest der Browser das Bild und übergibt echte Bilddaten an die KI.
    imageUrl = await imageUrlToDataUrl(item.url);
  } catch {
    return null;
  }
  const { data, error } = await supabase.functions.invoke("generate-image-metadata", {
    body: { image_url: imageUrl, filename: item.filename },
  });
  if (error || !data || (data as any).error) return null;
  const payload = data as { title?: string; alt?: string; description?: string; tags?: string[] };
  const updates: any = {
    title: payload.title || item.title,
    alt_text: payload.alt || item.alt_text,
    description: payload.description || item.description,
    tags: payload.tags && payload.tags.length ? Array.from(new Set([...item.tags, ...payload.tags])) : item.tags,
    metadata_generated: true,
  };
  const { data: updated, error: updErr } = await (supabase as any)
    .from("cms_media").update(updates).eq("id", item.id).select("*").single();
  if (updErr) return null;
  return updated as MediaItem;
}

export async function deleteMediaItem(item: MediaItem): Promise<void> {
  // Gebündelte Repo-Assets liegen nicht im Storage – nur den DB-Eintrag entfernen.
  if (!item.storage_path.startsWith("bundled:")) {
    await supabase.storage.from("cms-media").remove([item.storage_path]);
  }
  await (supabase as any).from("cms_media").delete().eq("id", item.id);
}

/** Listet alle Objekte im Bucket `cms-media` rekursiv. */
async function listAllStorage(prefix = ""): Promise<{ path: string; size: number | null; mimetype: string | null; created_at: string | null }[]> {
  const results: { path: string; size: number | null; mimetype: string | null; created_at: string | null }[] = [];
  const pageSize = 1000;
  let offset = 0;
  // paginate
  for (;;) {
    const { data, error } = await supabase.storage.from("cms-media").list(prefix, {
      limit: pageSize, offset, sortBy: { column: "name", order: "asc" },
    });
    if (error) throw error;
    if (!data || data.length === 0) break;
    for (const entry of data) {
      // Ordner haben id === null
      const isFolder = (entry as any).id === null || (entry as any).metadata === null;
      const childPath = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (isFolder) {
        const sub = await listAllStorage(childPath);
        results.push(...sub);
      } else {
        const meta: any = (entry as any).metadata ?? {};
        results.push({
          path: childPath,
          size: typeof meta.size === "number" ? meta.size : null,
          mimetype: typeof meta.mimetype === "string" ? meta.mimetype : null,
          created_at: (entry as any).created_at ?? null,
        });
      }
    }
    if (data.length < pageSize) break;
    offset += pageSize;
  }
  return results;
}

export type BackfillResult = { scanned: number; imported: number; skipped: number; errors: number };

/**
 * Importiert alle Bilder/Videos, die irgendwo auf der Seite genutzt werden:
 *  – sämtliche Objekte aus dem `cms-media`-Bucket
 *  – alle gebündelten Repo-Assets unter `src/assets` (inkl. CDN-Pointer `.asset.json`)
 * Bereits registrierte Einträge (gleicher `storage_path`) werden übersprungen.
 */
export async function backfillExistingMedia(opts: { generateMetadata?: boolean; onProgress?: (done: number, total: number) => void } = {}): Promise<BackfillResult> {
  // 1) Storage-Bucket scannen
  const all = await listAllStorage("");
  const storageItems = all.filter((e) => {
    const ext = e.path.split(".").pop()?.toLowerCase() ?? "";
    return ["jpg","jpeg","png","webp","gif","avif","svg","mp4","webm","mov","m4v"].includes(ext)
      || (e.mimetype?.startsWith("image/") ?? false)
      || (e.mimetype?.startsWith("video/") ?? false);
  });

  // 2) Gebündelte Repo-Assets einsammeln
  const { listBundledAssets } = await import("./bundledAssets");
  const bundled = listBundledAssets();

  // 3) Bereits bekannte Pfade ermitteln
  const { data: existing } = await (supabase as any)
    .from("cms_media").select("storage_path");
  const known = new Set<string>(((existing as { storage_path: string }[]) ?? []).map((r) => r.storage_path));

  const { data: { user } } = await supabase.auth.getUser();

  type Pending = {
    storage_path: string; url: string; filename: string; folder: string;
    mime: string | null; size: number | null;
  };

  const pending: Pending[] = [];

  for (const e of storageItems) {
    if (known.has(e.path)) continue;
    try {
      const { data: signed, error: signErr } = await supabase.storage
        .from("cms-media").createSignedUrl(e.path, 60 * 60 * 24 * 365 * 10);
      if (signErr || !signed) continue;
      const filename = e.path.split("/").pop() ?? e.path;
      const parts = e.path.split("/");
      pending.push({
        storage_path: e.path,
        url: signed.signedUrl,
        filename,
        folder: parts.length > 1 ? parts[0] : "",
        mime: e.mimetype,
        size: e.size,
      });
    } catch { /* skip */ }
  }

  for (const b of bundled) {
    if (known.has(b.key)) continue;
    pending.push({
      storage_path: b.key,
      url: b.url,
      filename: b.filename,
      folder: b.folder,
      mime: b.mime,
      size: null,
    });
  }

  const totalScanned = storageItems.length + bundled.length;
  const result: BackfillResult = {
    scanned: totalScanned,
    imported: 0,
    skipped: totalScanned - pending.length,
    errors: 0,
  };

  for (let i = 0; i < pending.length; i++) {
    const entry = pending[i];
    try {
      const baseName = entry.filename.replace(/\.[^.]+$/, "").replace(/^\d{10,}-/, "");
      const insertPayload = {
        storage_path: entry.storage_path,
        url: entry.url,
        filename: entry.filename,
        mime_type: entry.mime,
        size_bytes: entry.size,
        width: null,
        height: null,
        folder: entry.folder,
        tags: [],
        title: baseName,
        alt_text: "",
        description: "",
        metadata_generated: false,
        uploaded_by: user?.id ?? null,
      };
      const { data: row, error: insErr } = await (supabase as any)
        .from("cms_media").insert(insertPayload).select("*").single();
      if (insErr) throw insErr;
      result.imported++;
      // KI-Metadaten nur für echte Rasterbilder; SVG/GIF/Videos werden sicher übersprungen.
      if (opts.generateMetadata !== false && canGenerateImageMetadata({ filename: entry.filename, mime_type: entry.mime })) {
        void generateMetadataFor(row as MediaItem).catch(() => {});
      }
    } catch {
      result.errors++;
    }
    opts.onProgress?.(i + 1, pending.length);
  }
  return result;
}
